// franklycatering #1429 — data for the Events pipeline and the one-event page.
//
// Every query key starts with "events", so the ["events"] invalidations the
// quote and event hooks already make (create, duplicate, delete, status,
// assign, notes, outcome) refresh these views too.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@/integrations/supabase/types";
import type { EventOutcome, EventStatus, EventType } from "@/hooks/useEvents";
import {
  OPEN_STAGES,
  CLOSED_STAGES,
  draftDiscardable,
  normalizeStage,
  sanitizeSearchTerm,
  type StageQuote,
} from "@/lib/event-pipeline";
import { eventContactId, serviceTypeForEvent } from "@/lib/event-board";
import type { DateWindow } from "@/lib/event-filters";

/** Most open events the board loads (#1557: prod had 1,147 open on 2026-10-28). */
const PIPELINE_OPEN_LIMIT = 2000;
/** Rows per open-events request: PostgREST's max-rows on Supabase. */
const OPEN_PAGE = 1000;
/** Most closed (done, lost, cancelled) events loaded for the When filter's dates, newest first. */
export const PIPELINE_CLOSED_LIMIT = 300;
/** Event ids per `in()` request, which keeps the request URL short. */
const ID_CHUNK = 100;

/** Without the #1557 wrap-up columns: what prod has until that migration deploys. */
const PIPELINE_EVENT_COLUMNS_LEGACY =
  "id, event_number, client_name, client_email, contact_id, event_type, fulfillment_type, occasion, event_date, guest_count, status, outcome, stage, is_partial, created_at";
const PIPELINE_EVENT_COLUMNS = `${PIPELINE_EVENT_COLUMNS_LEGACY}, thank_you_sent_at, review_requested_at`;
const PIPELINE_QUOTE_COLUMNS =
  "id, event_id, quote_number, status, pipeline_stage, is_lost, total, amount_paid, payment_due_date, deposit_percent, created_at";
/** The board also reads each quote's service and contact; useEventQuotes selects those itself. */
const BOARD_QUOTE_COLUMNS = `${PIPELINE_QUOTE_COLUMNS}, service_type, contact_id`;

export interface PipelineQuote extends StageQuote {
  event_id: string;
  service_type?: string;
  contact_id?: string | null;
}

export interface PipelineEvent {
  id: string;
  event_number: string;
  client_name: string | null;
  client_email: string | null;
  contact_id: string | null;
  event_type: EventType;
  fulfillment_type: string | null;
  occasion: string | null;
  event_date: string | null;
  guest_count: number | null;
  status: EventStatus;
  outcome: EventOutcome;
  stage: string | null;
  is_partial: boolean;
  thank_you_sent_at: string | null;
  review_requested_at: string | null;
  created_at: string;
  quotes: PipelineQuote[];
}

/** A contact's newest message each way (kept by trg_contact_messages_bump_contact_last_at). */
export interface ContactLastMessage {
  last_inbound_at: string | null;
  last_outbound_at: string | null;
}

export interface EventsPipelineData {
  events: PipelineEvent[];
  /** Fewer open events loaded than match the search: the cap, or a short read. */
  capped: boolean;
  /** The last loaded open event's date (null: undated, or nothing loaded). The read is soonest first. */
  openBoundary: string | null;
  /** Every closed event in the When window matching the search, loaded or not. */
  closedCount: number;
  closedCapped: boolean;
  /** By contact id, for Lead and Quoting events only. */
  lastMessages: Record<string, ContactLastMessage>;
  /** The contacts read failed: no "No reply" flags. */
  messagesUnavailable: boolean;
  /** The database has no wrap-up columns yet (#1557 not deployed): "Mark sent" is off. */
  wrapUpUnavailable: boolean;
}

function chunks<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Quotes for the given events, fetched ID_CHUNK events at a time. */
async function quotesForEvents(eventIds: string[]): Promise<PipelineQuote[]> {
  const results = await Promise.all(
    chunks(eventIds, ID_CHUNK).map((ids) =>
      supabase.from("quotes").select(BOARD_QUOTE_COLUMNS).in("event_id", ids).order("created_at"),
    ),
  );
  const quotes: PipelineQuote[] = [];
  for (const r of results) {
    if (r.error) throw r.error;
    quotes.push(...((r.data ?? []) as PipelineQuote[]));
  }
  return quotes;
}

/** Events whose quote number matches the term, for the board's search. */
async function eventIdsForQuoteNumber(term: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("quotes")
    .select("event_id")
    .ilike("quote_number", `%${term}%`)
    .not("event_id", "is", null)
    .limit(50);
  if (error) throw error;
  return [...new Set((data ?? []).map((r) => r.event_id as string))];
}

type EventRowBase = Omit<PipelineEvent, "quotes">;

interface EventsRead {
  rows: EventRowBase[];
  count: number;
}

/**
 * The search as one or() filter. `term` has had , ( ) % * \ " ' removed, so it
 * can't break out of the or() grammar; the ids are uuids from the database.
 */
function searchFilterFor(term: string, matchIds: string[]): string | null {
  if (!term) return null;
  return [
    `client_name.ilike.%${term}%`,
    `client_email.ilike.%${term}%`,
    `event_number.ilike.%${term}%`,
    `occasion.ilike.%${term}%`,
    ...(matchIds.length ? [`id.in.(${matchIds.join(",")})`] : []),
  ].join(",");
}

/** Open-stage events (and NULL-stage ones), soonest first. */
async function fetchOpen(columns: string, searchFilter: string | null, includePartial: boolean): Promise<EventsRead> {
  // One page per request; the id tiebreak keeps pages from overlapping.
  const page = (from: number, withCount: boolean) => {
    let q = supabase
      .from("events")
      .select(columns, withCount ? { count: "exact" } : undefined)
      .or(`stage.is.null,stage.in.(${OPEN_STAGES.join(",")})`);
    if (!includePartial) q = q.eq("is_partial", false);
    if (searchFilter) q = q.or(searchFilter);
    return q
      .order("event_date", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: false })
      .order("id", { ascending: true })
      .range(from, Math.min(from + OPEN_PAGE, PIPELINE_OPEN_LIMIT) - 1);
  };
  const first = await page(0, true);
  if (first.error) throw first.error;
  const rows = (first.data ?? []) as unknown as EventRowBase[];
  const count = first.count ?? rows.length;
  // The next page starts where the rows stop, so a server row cap below
  // OPEN_PAGE can't leave a gap; an empty page ends the read.
  while (rows.length < Math.min(count, PIPELINE_OPEN_LIMIT)) {
    const res = await page(rows.length, false);
    if (res.error) throw res.error;
    const more = (res.data ?? []) as unknown as EventRowBase[];
    if (!more.length) break;
    rows.push(...more);
  }
  return { rows, count };
}

/** Closed events inside the When filter's dates, newest first. */
async function fetchClosed(
  columns: string,
  searchFilter: string | null,
  includePartial: boolean,
  dateWindow: DateWindow,
): Promise<EventsRead> {
  let q = supabase.from("events").select(columns, { count: "exact" }).in("stage", CLOSED_STAGES);
  if (!includePartial) q = q.eq("is_partial", false);
  if (searchFilter) q = q.or(searchFilter);
  // The dates come from closedWindow(), always yyyy-mm-dd.
  if (dateWindow.includeUndated && dateWindow.from) q = q.or(`event_date.gte.${dateWindow.from},event_date.is.null`);
  else if (dateWindow.from) q = q.gte("event_date", dateWindow.from);
  if (dateWindow.to) q = q.lte("event_date", dateWindow.to);
  const res = await q.order("event_date", { ascending: false, nullsFirst: false }).limit(PIPELINE_CLOSED_LIMIT);
  if (res.error) throw res.error;
  const rows = (res.data ?? []) as unknown as EventRowBase[];
  return { rows, count: res.count ?? rows.length };
}

interface EventsQuery {
  searchFilter: string | null;
  includePartial: boolean;
  dateWindow: DateWindow;
}

/** Postgres "undefined_column": the select names a column the database doesn't have yet. */
function isMissingColumn(err: unknown): boolean {
  return !!err && typeof err === "object" && (err as { code?: unknown }).code === "42703";
}

/**
 * Both reads. Until the #1557 migration deploys, prod (which every Vercel
 * preview reads too) has no wrap-up columns and rejects the whole select, so
 * that one error retries once without them.
 */
async function fetchEventSets(args: EventsQuery) {
  const run = (columns: string) =>
    Promise.all([
      fetchOpen(columns, args.searchFilter, args.includePartial),
      fetchClosed(columns, args.searchFilter, args.includePartial, args.dateWindow),
    ]);
  try {
    const [open, closed] = await run(PIPELINE_EVENT_COLUMNS);
    return { open, closed, wrapUpUnavailable: false };
  } catch (err) {
    if (!isMissingColumn(err)) throw err;
    const [open, closed] = await run(PIPELINE_EVENT_COLUMNS_LEGACY);
    const fill = (r: EventsRead): EventsRead => ({
      ...r,
      rows: r.rows.map((e) => ({ ...e, thank_you_sent_at: null, review_requested_at: null })),
    });
    return { open: fill(open), closed: fill(closed), wrapUpUnavailable: true };
  }
}

/**
 * Each Lead and Quoting event's contact's newest message each way, for "No
 * reply". The board must not fail on this flag, so any error just turns it off.
 */
async function lastMessagesFor(
  events: PipelineEvent[],
): Promise<{ map: Record<string, ContactLastMessage>; unavailable: boolean }> {
  const ids = new Set<string>();
  for (const e of events) {
    const stage = normalizeStage(e.stage);
    if (stage !== "lead" && stage !== "quoting") continue;
    const id = eventContactId(e);
    if (id) ids.add(id);
  }
  if (ids.size === 0) return { map: {}, unavailable: false };
  const results = await Promise.all(
    chunks([...ids], ID_CHUNK).map((chunk) =>
      supabase.from("contacts").select("id, last_inbound_at, last_outbound_at").in("id", chunk),
    ),
  );
  const map: Record<string, ContactLastMessage> = {};
  for (const r of results) {
    if (r.error) return { map: {}, unavailable: true };
    for (const c of r.data ?? []) {
      map[c.id] = { last_inbound_at: c.last_inbound_at, last_outbound_at: c.last_outbound_at };
    }
  }
  return { map, unavailable: false };
}

/**
 * Rows from both reads, once each. The open and closed reads are separate
 * queries, so an event whose stage changes between them can come back in both.
 */
function uniqueById(rows: EventRowBase[]): EventRowBase[] {
  const seen = new Set<string>();
  return rows.filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)));
}

async function loadPipeline(
  term: string,
  dateWindow: DateWindow,
  includePartial: boolean,
): Promise<EventsPipelineData> {
  const matchIds = term ? await eventIdsForQuoteNumber(term) : [];
  const { open, closed, wrapUpUnavailable } = await fetchEventSets({
    searchFilter: searchFilterFor(term, matchIds),
    includePartial,
    dateWindow,
  });
  const rows = uniqueById([...open.rows, ...closed.rows]);
  const quotes = rows.length ? await quotesForEvents(rows.map((e) => e.id)) : [];
  const byEvent = new Map<string, PipelineQuote[]>();
  for (const q of quotes) {
    const list = byEvent.get(q.event_id) ?? [];
    list.push(q);
    byEvent.set(q.event_id, list);
  }
  const events = rows.map((e) => ({ ...e, quotes: byEvent.get(e.id) ?? [] }));
  const messages = await lastMessagesFor(events);
  return {
    events,
    capped: open.rows.length < open.count,
    openBoundary: open.rows.length ? open.rows[open.rows.length - 1].event_date : null,
    closedCount: closed.count,
    closedCapped: closed.count > PIPELINE_CLOSED_LIMIT,
    lastMessages: messages.map,
    messagesUnavailable: messages.unavailable,
    wrapUpUnavailable,
  };
}

/**
 * The Events board (#1557): every open event, plus the closed ones inside the
 * When filter's dates. The other filters run on the loaded rows.
 */
export function useEventsPipeline({
  search,
  window: dateWindow,
  includePartial,
}: {
  search: string;
  window: DateWindow;
  includePartial: boolean;
}) {
  const term = sanitizeSearchTerm(search);
  return useQuery({
    queryKey: ["events", "pipeline", { search: term, window: dateWindow, includePartial }],
    queryFn: () => loadPipeline(term, dateWindow, includePartial),
    placeholderData: (prev) => prev,
  });
}

export interface EventQuoteLineItem {
  id: string;
  name: string;
  quantity: number;
  line_total: number;
  sort_order: number;
}

export interface EventPageQuote extends PipelineQuote {
  contact_id: string | null;
  service_type: string;
  line_items: EventQuoteLineItem[];
}

/** Every quote on one event, with its line items, oldest first. */
export function useEventQuotes(eventId: string | null | undefined) {
  return useQuery({
    queryKey: ["events", "quotes", eventId],
    enabled: !!eventId,
    queryFn: async (): Promise<EventPageQuote[]> => {
      const { data, error } = await supabase
        .from("quotes")
        .select(
          `${PIPELINE_QUOTE_COLUMNS}, contact_id, service_type, line_items:quote_line_items(id, name, quantity, line_total, sort_order)`,
        )
        .eq("event_id", eventId as string)
        .order("created_at");
      if (error) throw error;
      return ((data ?? []) as EventPageQuote[]).map((q) => ({
        ...q,
        line_items: [...(q.line_items ?? [])].sort((a, b) => a.sort_order - b.sort_order),
      }));
    },
  });
}

export type EventPayment = Pick<
  Database["public"]["Tables"]["quote_payments"]["Row"],
  "id" | "quote_id" | "amount" | "method" | "payment_date" | "reference"
>;

/** quote_payments across the event's quotes, newest first. */
export function useEventPayments(eventId: string | null | undefined, quoteIds: string[]) {
  return useQuery({
    queryKey: ["events", "payments", eventId, quoteIds],
    enabled: !!eventId && quoteIds.length > 0,
    queryFn: async (): Promise<EventPayment[]> => {
      const { data, error } = await supabase
        .from("quote_payments")
        .select("id, quote_id, amount, method, payment_date, reference")
        .in("quote_id", quoteIds)
        .order("payment_date", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export type EventQuoteResponse = Pick<
  Database["public"]["Tables"]["quote_responses"]["Row"],
  "id" | "quote_id" | "action" | "respondent_name" | "message" | "created_at"
>;

/** Client responses (accept, decline, revision requests) on the event's quotes. */
export function useEventQuoteActivity(eventId: string | null | undefined, quoteIds: string[]) {
  return useQuery({
    queryKey: ["events", "quote-activity", eventId, quoteIds],
    enabled: !!eventId && quoteIds.length > 0,
    queryFn: async (): Promise<EventQuoteResponse[]> => {
      const { data, error } = await supabase
        .from("quote_responses")
        .select("id, quote_id, action, respondent_name, message, created_at")
        .in("quote_id", quoteIds)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Refresh the board and event pages after a change made inside the quote panel. */
export function invalidateEventViews(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ["events"] });
  qc.invalidateQueries({ queryKey: ["event-detail"] });
}

export interface CreatedEventQuote {
  id: string;
  quote_number: string;
  event_number: string;
  /**
   * False when deleting the draft would fire trg_cleanup_manual_quote_event,
   * which removes or cancels a `manual` event once its last quote goes.
   */
  discardable: boolean;
}

/**
 * "+ Quote": a draft quote already linked to the event. It is inserted here,
 * as useDuplicateQuote does, because useCreateQuote (NewQuoteDialog) would give
 * the quote an event of its own. No quotes INSERT trigger creates an event when
 * event_id is set.
 */
export function useCreateEventQuote() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ eventId }: { eventId: string }): Promise<CreatedEventQuote> => {
      const { data: event, error: eventErr } = await supabase
        .from("events")
        .select(
          "id, event_number, client_name, client_email, client_phone, contact_id, event_date, event_time, delivery_address, guest_count, event_type, fulfillment_type, source",
        )
        .eq("id", eventId)
        .single();
      if (eventErr) throw eventErr;

      const { data: prior, error: priorErr } = await supabase
        .from("quotes")
        .select("id, service_type, created_at")
        .eq("event_id", eventId)
        .order("created_at", { ascending: false });
      if (priorErr) throw priorErr;

      const { data, error } = await supabase
        .from("quotes")
        .insert({
          quote_number: "", // set_quote_number fills it
          event_id: event.id,
          status: "draft",
          client_name: event.client_name?.trim() || "(no name)",
          client_email: event.client_email,
          client_phone: event.client_phone,
          contact_id: event.contact_id,
          event_date: event.event_date,
          event_start_time: event.event_time,
          event_address: event.delivery_address,
          guest_count: event.guest_count ?? 1,
          service_type: serviceTypeForEvent(event, prior?.[0]?.service_type),
          created_by: user?.id,
          updated_by: user?.id,
        })
        .select("id, event_id, quote_number")
        .single();
      if (error) throw error;

      return {
        id: data.id,
        quote_number: data.quote_number,
        event_number: event.event_number,
        discardable: draftDiscardable(event.source, prior?.length ?? 0),
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
    },
    onError: (e: Error) => toast.error(e.message || "Couldn't add a quote"),
  });
}

export type EventDetailsPatch = Partial<
  Pick<
    Database["public"]["Tables"]["events"]["Update"],
    | "event_date"
    | "event_time"
    | "guest_count"
    | "delivery_address"
    | "client_name"
    | "client_email"
    | "client_phone"
  >
>;

/**
 * Saves the event's details. The #1428 trigger copies them to the event's
 * quotes that aren't declined.
 */
export function useUpdateEventDetails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: EventDetailsPatch }) => {
      if (Object.keys(patch).length === 0) return;
      const { error } = await supabase.from("events").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["event-detail", id] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
    },
  });
}

export type WrapUpField = "thank_you_sent_at" | "review_requested_at";

/**
 * "Mark sent" on a Post-event card. Only sets a timestamp that is still empty,
 * so a second click (or a second person) changes nothing. Resolves to whether
 * a row changed: the caller toasts "Thank-you marked sent." / "Review ask
 * marked sent." when one did, and "Already marked sent." when none did.
 */
export function useMarkEventWrapUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, field }: { id: string; field: WrapUpField }): Promise<boolean> => {
      const now = new Date().toISOString();
      // One branch per column, so each update type-checks against the table.
      const { data, error } =
        field === "thank_you_sent_at"
          ? await supabase
              .from("events")
              .update({ thank_you_sent_at: now })
              .eq("id", id)
              .is("thank_you_sent_at", null)
              .select("id")
          : await supabase
              .from("events")
              .update({ review_requested_at: now })
              .eq("id", id)
              .is("review_requested_at", null)
              .select("id");
      if (error) throw error;
      return (data ?? []).length > 0;
    },
    onSuccess: (_changed, { id }) => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["event-detail", id] });
    },
    onError: () => toast.error("Couldn't mark it sent"),
  });
}

/**
 * "Mark done" on a Post-event card: status completed, which derives Done.
 * Deliberately outside VALID_TRANSITIONS (which only allows completed from
 * in_progress); the status guard keeps it off an event already closed.
 */
export function useMarkEventDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { data, error } = await supabase
        .from("events")
        .update({ status: "completed" })
        .eq("id", id)
        .not("status", "in", "(completed,cancelled,lost)")
        .select("id");
      if (error) throw error;
      if ((data ?? []).length === 0) throw new Error("This event is already closed.");
    },
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["event-detail", id] });
    },
    onError: (e: Error) => toast.error(e.message || "Couldn't mark it done"),
  });
}

/**
 * Whether deleting this draft leaves its event alone: false when it is a
 * `manual` event's only quote (trg_cleanup_manual_quote_event would remove or
 * cancel the event). A quote with no event is always deletable here.
 */
export async function fetchDraftDeletable(eventId: string | null | undefined, quoteId: string): Promise<boolean> {
  if (!eventId) return true;
  const [event, others] = await Promise.all([
    supabase.from("events").select("source").eq("id", eventId).maybeSingle(),
    supabase.from("quotes").select("id", { count: "exact", head: true }).eq("event_id", eventId).neq("id", quoteId),
  ]);
  if (event.error) throw event.error;
  if (others.error) throw others.error;
  return draftDiscardable(event.data?.source ?? null, others.count ?? 0);
}

/** fetchDraftDeletable for the quote open in the panel (EventQuotePanel's Delete draft). */
export function useDraftDeletable(quote: { id: string; event_id?: string | null } | null) {
  return useQuery({
    queryKey: ["events", "draft-deletable", quote?.id],
    enabled: !!quote,
    queryFn: () => (quote ? fetchDraftDeletable(quote.event_id, quote.id) : Promise.resolve(false)),
  });
}
